import { Link } from "react-router-dom";
import { PawPrint, ArrowRight, Phone, MessageSquare, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import PawDivider from "@/components/PawDivider";
import BenjiSeal from "@/components/BenjiSeal";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/Reveal";
import { Icon } from "@/components/iconMap";
import { EstimateButton, CallButton, TextButton } from "@/components/CTAButtons";
import {
  SectionHeading, EstimateCTASection, DogTreatSection, WhyChooseSection,
  BeforeAfterSection, ReviewsSection, ServiceAreaSection, MarqueeBand,
} from "@/components/Sections";
import { services, trustPoints, howItWorks, telLink, smsLink, business } from "@/data/site";
import { MapPin } from "lucide-react";

const heroLines = ["Junk Gone.", "Stress Gone.", "Benji Approved."];

export default function Home() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: business.name,
    description: "Friendly, reliable full-service junk removal and property cleanouts with straightforward pricing starting at $99.",
    url: "https://benjishauling.com/",
    telephone: business.phoneHref,
    email: business.email,
    priceRange: "$$",
    areaServed: "Salem, OR and surrounding areas",
    address: { "@type": "PostalAddress", addressLocality: business.city, addressRegion: business.state },
    sameAs: [business.social.instagram.split("?")[0], business.social.facebook.split("?")[0]],
  };

  return (
    <>
      <Seo
        title="Junk Removal & Hauling | Benji's Hauling & Junk Removal"
        description="Fast, friendly & reliable junk removal and property cleanouts. Free estimates and pricing starting at $99, with a complimentary treat bag for your dog. Benji Approved."
        path="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-cream">
        <div className="container-x grid items-center gap-12 py-14 lg:grid-cols-[1.05fr_0.95fr] lg:py-20">
          <div>
            <div className="animate-fade-up">
              <BenjiSeal />
            </div>

            <h1 className="mt-6 font-heading text-[2.6rem] font-extrabold leading-[1.05] tracking-tight text-forest sm:text-6xl">
              {heroLines.map((line, i) => (
                <span
                  key={line}
                  className={`animate-fade-up block ${i === 2 ? "text-gold-dark" : ""}`}
                  style={{ animationDelay: `${0.1 + i * 0.12}s` }}
                >
                  <span className="inline-flex items-center gap-3">
                    {line}
                    {i === 2 && <PawPrint className="h-8 w-8 text-gold sm:h-10 sm:w-10" strokeWidth={2.2} />}
                  </span>
                </span>
              ))}
            </h1>

            <p className="animate-fade-up mt-3 font-heading text-lg font-bold text-mutedgreen" style={{ animationDelay: "0.5s" }}>
              Fast, Friendly &amp; Reliable Junk Removal
            </p>

            <p className="animate-fade-up mt-3 inline-flex items-center gap-2 rounded-md bg-forest/5 px-3.5 py-1.5 text-sm font-semibold text-forest" style={{ animationDelay: "0.55s" }}>
              <MapPin className="h-4 w-4 text-gold-dark" strokeWidth={2.2} /> Serving Salem &amp; surrounding areas
            </p>

            <p className="animate-fade-up mt-4 max-w-xl text-base leading-relaxed text-charcoal/75" style={{ animationDelay: "0.6s" }}>
              From single-item pickups to complete property cleanouts, Benji's Hauling &amp; Junk Removal makes
              getting rid of unwanted junk simple. Get dependable service, straightforward pricing, and a free
              estimate, with a little extra love for your four-legged family members.
            </p>

            <div className="animate-fade-up mt-8 flex flex-wrap gap-3" style={{ animationDelay: "0.7s" }}>
              <EstimateButton label="Get a Free Estimate" size="lg" />
              <CallButton size="lg" />
              <TextButton size="lg" />
            </div>
          </div>

          {/* Hero image with clipped frame + floating accents */}
          <div className="animate-fade-up relative" style={{ animationDelay: "0.25s" }}>
            <div className="absolute -right-6 -top-6 hidden h-28 w-28 rounded-md border-2 border-gold/40 lg:block" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-lg border border-border shadow-sm group">
              <img
                src="https://images.unsplash.com/photo-1772544376426-6a42f1dfa74a?auto=format&fit=crop&w=1000&q=75"
                alt="Junk removal trailer loaded with furniture ready for hauling"
                width="1000" height="750" fetchPriority="high"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/25 to-transparent" aria-hidden="true" />
            </div>
            <div className="animate-float absolute -bottom-5 left-4 flex items-center gap-3 rounded-md border border-border bg-white px-4 py-3 shadow-md sm:left-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-gold/15 text-gold-dark">
                <PawPrint className="h-5 w-5" strokeWidth={2.2} />
              </span>
              <div>
                <p className="font-heading text-sm font-extrabold text-forest">Starting at ${business.startingPrice}</p>
                <p className="text-xs text-mutedgreen">Free estimates, always</p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div className="border-t border-border bg-white/70">
          <div className="container-x flex flex-wrap items-center justify-center gap-x-6 gap-y-2 py-4 sm:justify-between">
            {trustPoints.map((t) => (
              <span key={t.label} className="inline-flex items-center gap-2 text-sm font-semibold text-forest/80">
                {t.icon === "paw"
                  ? <PawPrint className="h-4 w-4 text-gold-dark" strokeWidth={2.2} />
                  : <CheckCircle2 className="h-4 w-4 text-mutedgreen" strokeWidth={2.2} />}
                {t.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-cream py-20">
        <div className="container-x">
          <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
            <Reveal>
              <SectionHeading
                eyebrow="Welcome"
                title="Welcome to Benji's Hauling & Junk Removal"
              />
              <div className="mt-5 space-y-4 text-base leading-relaxed text-charcoal/75">
                <p>
                  Inspired by man's best friend, we believe great service should be loyal, reliable, and always
                  ready to help. Whether you need one bulky item removed or an entire property cleaned out,
                  Benji's Hauling &amp; Junk Removal makes the process simple from start to finish.
                </p>
                <p>
                  We show up ready to work, provide free estimates, haul away the mess, and leave your space
                  cleaner than we found it.
                </p>
                <p>
                  And because Benji believes every good helper deserves a reward, customers with a furry friend
                  receive a complimentary treat bag for their dog with their booked service.
                </p>
              </div>
              <div className="mt-7">
                <EstimateButton label="Get Your Free Estimate" size="lg" />
              </div>
            </Reveal>
            <Reveal delay={0.1} className="relative">
              <div className="group overflow-hidden rounded-lg border border-border">
                <img
                  src="https://images.unsplash.com/photo-1657049199023-87fb439d47c5?auto=format&fit=crop&w=800&q=72"
                  alt="Crew carefully carrying furniture during a cleanout"
                  width="800" height="900" loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <PawDivider className="bg-cream" />

      {/* SERVICES PREVIEW */}
      <section className="bg-accent/40 py-20">
        <div className="container-x">
          <Reveal>
            <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
              <SectionHeading eyebrow="What we haul" title="Junk Removal for Every Kind of Job" sub="From a single couch to a full property cleanout, we handle the heavy lifting, loading, and cleanup." />
              <Link to="/services" data-testid="services-viewall" className="inline-flex shrink-0 items-center gap-1.5 font-heading text-sm font-bold text-gold-dark hover:gap-2.5 transition-all">
                View all services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
          <StaggerGroup className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {services.slice(0, 6).map((s) => (
              <StaggerItem key={s.slug}>
                <Link to="/services" className="group flex h-full flex-col rounded-lg border border-border bg-white p-6 transition-all hover:-translate-y-1 hover:border-gold/50 hover:shadow-md">
                  <span className="flex h-12 w-12 items-center justify-center rounded-md bg-forest/5 text-forest transition-colors group-hover:bg-gold/15 group-hover:text-gold-dark">
                    <Icon name={s.icon} className="h-6 w-6" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-forest">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-charcoal/70">{s.blurb}</p>
                </Link>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <Reveal className="mt-10 text-center">
            <EstimateButton label="Request a Free Estimate" size="lg" />
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-forest py-20 relative overflow-hidden">
        <div className="grain pointer-events-none absolute inset-0 opacity-[0.06]" aria-hidden="true" />
        <div className="container-x relative">
          <Reveal>
            <SectionHeading eyebrow="Simple process" title="How It Works" align="center" light sub="Three easy steps from clutter to clear space." />
          </Reveal>
          <StaggerGroup className="mt-12 grid gap-6 md:grid-cols-3">
            {howItWorks.map((h) => (
              <StaggerItem key={h.step} className="relative rounded-lg border border-cream/15 bg-cream/[0.04] p-7">
                <span className="font-heading text-5xl font-extrabold text-gold/70">{h.step}</span>
                <h3 className="mt-3 font-heading text-xl font-bold text-cream">{h.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-cream/70">{h.body}</p>
              </StaggerItem>
            ))}
          </StaggerGroup>
          <Reveal className="mt-12 text-center">
            <p className="font-heading text-xl font-bold text-cream sm:text-2xl">
              You point. We haul. <span className="text-gold">You enjoy the extra space.</span>
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href={telLink} className="inline-flex items-center gap-2 rounded-md bg-gold px-6 py-3.5 text-sm font-bold text-charcoal transition-all hover:-translate-y-0.5 hover:bg-gold-dark">
                <Phone className="h-4 w-4" /> Call Now
              </a>
              <a href={smsLink} className="inline-flex items-center gap-2 rounded-md border border-cream/30 px-6 py-3.5 text-sm font-bold text-cream transition-all hover:-translate-y-0.5 hover:bg-cream/10">
                <MessageSquare className="h-4 w-4" /> Text Us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <MarqueeBand />

      <DogTreatSection />
      <WhyChooseSection />
      <BeforeAfterSection />
      <ServiceAreaSection dark />
      <ReviewsSection />
      <EstimateCTASection />
    </>
  );
}
